import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import TemplateRenderer from './TemplateRenderer';
import { NICHES_CONFIG } from './niches';

const SectionTitle = ({ number, title, subtitle }) => (
  <div className="flex items-start gap-4 mb-6">
    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-brand-600 text-white text-sm font-black flex items-center justify-center">
      {number}
    </div>
    <div>
      <h3 className="font-bold text-gray-900 text-base leading-tight">{title}</h3>
      <p className="text-gray-400 text-xs mt-0.5">{subtitle}</p>
    </div>
  </div>
);

const WorkshopSetup = () => {
  const { workshopId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [workshop, setWorkshop] = useState(null);
  const [activeTab, setActiveTab] = useState('hero');
  const [successData, setSuccessData] = useState(null); // { url, slug }
  const [copied, setCopied] = useState(false);
  const [formData, setFormData] = useState({
    hero_title: '',
    hero_description: '',
    brand_color_primary: '#1e293b',
    brand_color_secondary: '#3b82f6',
    hero_image_url: '',
    footer_tagline: '',
    whatsapp: '',
    instagram: '',
    facebook: '',
    tiktok: '',
    services: [],
  });

  useEffect(() => {
    const fetchWorkshop = async () => {
      try {
        const { data, error } = await supabase
          .from('workshops')
          .select('id, name, slug, city, address, phone, opening_time, closing_time, niche')
          .eq('id', workshopId)
          .single();
        if (error) throw error;
        setWorkshop(data);

        // Cargamos la primera imagen del nicho elegido por defecto
        const nicheKey = data.niche && NICHES_CONFIG[data.niche] ? data.niche : 'otros';
        setFormData(prev => ({
          ...prev,
          hero_image_url: NICHES_CONFIG[nicheKey].images[0].url
        }));
      } catch (err) {
        console.error('Error cargando taller:', err.message);
      } finally {
        setFetching(false);
      }
    };
    fetchWorkshop();
  }, [workshopId]);

  const set = (key, val) => setFormData(prev => ({ ...prev, [key]: val }));

  const toggleService = (id) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(id)
        ? prev.services.filter(s => s !== id)
        : [...prev.services, id],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.services.length === 0) {
      alert('Selecciona al menos un servicio que ofrece tu taller.');
      setActiveTab('servicios');
      return;
    }
    setLoading(true);
    try {
      const { data: updated, error } = await supabase
        .from('workshops')
        .update({
          hero_title: formData.hero_title,
          hero_description: formData.hero_description,
          brand_color_primary: formData.brand_color_primary,
          brand_color_secondary: formData.brand_color_secondary,
          hero_image_url: formData.hero_image_url,
          footer_tagline: formData.footer_tagline,
          whatsapp: formData.whatsapp,
          services: formData.services,
          instagram: formData.instagram,
          facebook: formData.facebook,
          tiktok: formData.tiktok,
        })
        .eq('id', workshopId)
        .select();

      if (error) throw error;
      if (!updated || updated.length === 0) {
        throw new Error('La base de datos no actualizo el taller. Verifica los permisos RLS en Supabase (UPDATE policy para la tabla workshops).');
      }
      const landingUrl = `${window.location.origin}/taller/${workshop.slug}`;
      setSuccessData({ url: landingUrl, slug: workshop.slug, name: workshop.name });
    } catch (err) {
      console.error('Error guardando configuracion:', err.message);
      alert('Error al guardar: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-gray-500">Cargando tu taller...</p>
        </div>
      </div>
    );
  }

  if (!workshop) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500 text-lg">Taller no encontrado.</p>
      </div>
    );
  }

  const copyUrl = () => {
    navigator.clipboard.writeText(successData.url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  // ── Pantalla de éxito ──
  if (successData) {
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&color=000000&bgcolor=ffffff&data=${encodeURIComponent(successData.url)}`;
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl shadow-gray-200/80 border border-gray-100 w-full max-w-md p-8 text-center">

          {/* Icono éxito */}
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
            <svg className="w-8 h-8 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>

          <h1 className="text-2xl font-black text-gray-900 mb-1">Pagina publicada</h1>
          <p className="text-gray-400 text-sm mb-6">
            <span className="font-semibold text-gray-600">{successData.name}</span> ya esta en linea
          </p>

          {/* QR Code */}
          <div className="bg-gray-50 rounded-2xl p-5 mb-5 border border-gray-100">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">Codigo QR de tu pagina</p>
            <img
              src={qrUrl}
              alt="QR Code"
              className="w-44 h-44 mx-auto rounded-xl"
            />
            <p className="text-xs text-gray-400 mt-3">Comparte este QR con tus clientes para que agenden directamente</p>
          </div>

          {/* URL */}
          <div className="bg-brand-50 border border-brand-200 rounded-xl px-4 py-3 mb-5">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Link de tu pagina</p>
            <p className="text-brand-600 font-bold text-sm break-all">{successData.url}</p>
          </div>

          {/* Botones */}
          <div className="flex flex-col gap-3">
            <button
              onClick={copyUrl}
              className={`w-full py-3 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 ${
                copied
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
            >
              {copied ? (
                <>
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  Link copiado
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                  Copiar link
                </>
              )}
            </button>

            {workshop?.phone && (() => {
              const cleanPhone = workshop.phone.replace(/[\s\-\(\)\+]/g, '');
              const msg = encodeURIComponent(
                `¡Hola! 👋 Tu página web *${successData.name}* ya está publicada y lista para recibir clientes.\n\n` +
                `🔗 *Este es tu link:*\n${successData.url}\n\n` +
                `Guárdalo y compártelo con tus clientes para que puedan ver tus servicios y agendar directamente. 🚀\n\n` +
                `También puedes mostrar el código QR que aparece en tu pantalla de configuración.`
              );
              return (
                <a
                  href={`https://wa.me/${cleanPhone}?text=${msg}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-3 rounded-xl font-bold text-sm text-white transition-all flex items-center justify-center gap-2 shadow-lg hover:opacity-90"
                  style={{ backgroundColor: '#25D366', boxShadow: '0 4px 16px rgba(37,211,102,0.35)' }}
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  Enviar link por WhatsApp
                </a>
              );
            })()}

            <button
              onClick={() => navigate(`/taller/${successData.slug}`)}
              className="w-full py-3 rounded-xl font-bold text-sm bg-brand-600 hover:bg-brand-500 text-white transition-all flex items-center justify-center gap-2 shadow-lg shadow-brand-600/20"
            >
              Ver mi pagina web
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
            </button>
          </div>

          <p className="text-xs text-gray-400 mt-5">
            Guarda el QR o el link — es el acceso directo de tus clientes para agendar
          </p>
        </div>
      </div>
    );
  }

  const primary = formData.brand_color_primary;
  const secondary = formData.brand_color_secondary;
  const heroImg = formData.hero_image_url;

  // Obtenemos las imágenes correspondientes al nicho actual del negocio
  const currentNicheKey = workshop?.niche && NICHES_CONFIG[workshop.niche] ? workshop.niche : 'otros';
  const currentNicheImages = NICHES_CONFIG[currentNicheKey].images;
  const currentNicheServices = NICHES_CONFIG[currentNicheKey].services;

  const TABS = [
    { id: 'hero',      label: 'Texto',     icon: '✏️' },
    { id: 'imagen',    label: 'Imagen',    icon: '🖼️' },
    { id: 'servicios', label: 'Servicios', icon: '🛠️' },
    { id: 'colores',   label: 'Colores',   icon: '🎨' },
    { id: 'footer',    label: 'Footer',    icon: '📋' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 font-sans">

      {/* TOP BAR */}
      <div className="bg-white border-b border-gray-100 px-6 py-3.5 flex items-center justify-between shadow-sm sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-brand-600 rounded-lg flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
            </svg>
          </div>
          <div>
            <p className="font-bold text-gray-900 text-sm leading-tight">Next Pro 360</p>
            <p className="text-gray-400 text-xs">Personaliza tu pagina web</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs bg-brand-100 text-brand-600 font-semibold px-3 py-1 rounded-full hidden sm:block">Paso 2 de 2</span>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-white text-sm transition-all ${
              loading ? 'bg-gray-300 cursor-not-allowed' : 'bg-brand-600 hover:bg-brand-500 shadow-md shadow-brand-600/20'
            }`}
          >
            {loading ? 'Publicando...' : (
              <>
                Publicar pagina
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                </svg>
              </>
            )}
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* WELCOME */}
        <div className="mb-8">
          <h1 className="text-2xl font-black text-gray-900">
            Hola, <span className="text-brand-600">{workshop.name}</span> 👋
          </h1>
          <p className="text-gray-500 mt-1">Tu taller esta registrado. Ahora crea tu pagina web en 5 pasos.</p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 items-start">

          {/* FORM */}
          <div className="lg:col-span-2 space-y-4">

            {/* TABS */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="grid grid-cols-5 border-b border-gray-100">
                {TABS.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-3 text-xs font-bold flex flex-col items-center gap-1 transition-colors relative ${
                      activeTab === tab.id
                        ? 'text-brand-600 bg-brand-50 border-b-2 border-brand-600'
                        : 'text-gray-400 hover:text-gray-600'
                    }`}
                  >
                    <span className="text-base">{tab.icon}</span>
                    {tab.label}
                    {tab.id === 'servicios' && formData.services.length > 0 && (
                      <span className="absolute top-1.5 right-1.5 w-4 h-4 bg-brand-500 rounded-full text-white text-xs flex items-center justify-center font-black leading-none">
                        {formData.services.length}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              <div className="p-6">

                {/* TAB: TEXTO */}
                {activeTab === 'hero' && (
                  <div className="space-y-5">
                    <SectionTitle number="1" title="Texto principal" subtitle="Lo primero que vera el cliente al entrar" />
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Titulo de tu pagina</label>
                      <input
                        type="text"
                        placeholder="Ej: Expertos en mecanica automotriz en Chia"
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all text-sm"
                        value={formData.hero_title}
                        onChange={(e) => set('hero_title', e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Descripcion / Slogan</label>
                      <textarea
                        rows={3}
                        placeholder="Ej: Cuidamos tu vehiculo con tecnologia de punta y garantia en cada trabajo."
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all text-sm resize-none"
                        value={formData.hero_description}
                        onChange={(e) => set('hero_description', e.target.value)}
                      />
                    </div>
                  </div>
                )}

                {/* TAB: IMAGEN */}
                {activeTab === 'imagen' && (
                  <div className="space-y-5">
                    <SectionTitle number="2" title="Imagen de fondo" subtitle="Elige la foto que mas represente tu taller" />
                    <div className="grid grid-cols-3 gap-2.5">
                      {currentNicheImages.map((preset) => (
                        <button
                          key={preset.url}
                          type="button"
                          onClick={() => set('hero_image_url', preset.url)}
                          className={`relative rounded-xl overflow-hidden border-2 transition-all group ${
                            formData.hero_image_url === preset.url
                              ? 'border-brand-500 shadow-lg shadow-brand-500/20'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <img src={preset.thumb} alt={preset.label} className="w-full h-20 object-cover" />
                          <div className={`absolute inset-0 flex items-end p-2 ${
                            formData.hero_image_url === preset.url ? 'bg-brand-600/40' : 'bg-black/20 group-hover:bg-black/30'
                          }`}>
                            <span className="text-white text-xs font-bold">{preset.label}</span>
                          </div>
                          {formData.hero_image_url === preset.url && (
                            <div className="absolute top-2 right-2 w-5 h-5 bg-brand-500 rounded-full flex items-center justify-center">
                              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">O pega el URL de tu propia foto</label>
                      <input
                        type="url"
                        placeholder="https://tu-imagen.com/foto.jpg"
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all text-sm"
                        value={currentNicheImages.some(p => p.url === formData.hero_image_url) ? '' : formData.hero_image_url}
                        onChange={(e) => e.target.value && set('hero_image_url', e.target.value)}
                      />
                    </div>
                  </div>
                )}

                {/* TAB: SERVICIOS */}
                {activeTab === 'servicios' && (
                  <div className="space-y-4">
                    <SectionTitle number="3" title="Servicios que ofreces" subtitle="Solo se mostraran los que selecciones" />

                    {/* Counter */}
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-gray-400">
                        {formData.services.length === 0
                          ? 'Selecciona al menos 1 servicio'
                          : `${formData.services.length} servicio${formData.services.length > 1 ? 's' : ''} seleccionado${formData.services.length > 1 ? 's' : ''}`}
                      </p>
                      {formData.services.length > 0 && (
                        <button
                          type="button"
                          onClick={() => set('services', [])}
                          className="text-xs text-red-400 hover:text-red-600 font-medium transition-colors"
                        >
                          Limpiar
                        </button>
                      )}
                    </div>

                    {/* Grid de servicios */}
                    <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                      {currentNicheServices.map((service) => {
                        const selected = formData.services.includes(service.id);
                        return (
                          <button
                            key={service.id}
                            type="button"
                            onClick={() => toggleService(service.id)}
                            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 text-left transition-all ${
                              selected
                                ? 'border-brand-400 bg-brand-50'
                                : 'border-gray-100 bg-gray-50 hover:border-gray-200'
                            }`}
                          >
                            <span className="text-xl flex-shrink-0">{service.icon}</span>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-bold leading-tight ${selected ? 'text-brand-700' : 'text-gray-700'}`}>
                                {service.title}
                              </p>
                              <p className="text-xs text-gray-400 truncate mt-0.5">{service.desc}</p>
                            </div>
                            <div className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                              selected ? 'bg-brand-500 border-brand-500' : 'border-gray-300'
                            }`}>
                              {selected && (
                                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* TAB: COLORES */}
                {activeTab === 'colores' && (
                  <div className="space-y-5">
                    <SectionTitle number="4" title="Identidad visual" subtitle="Los colores de tu marca" />
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Color del Header y Overlay</label>
                      <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl">
                        <input
                          type="color"
                          className="w-10 h-10 rounded-lg cursor-pointer border-none bg-transparent"
                          value={formData.brand_color_primary}
                          onChange={(e) => set('brand_color_primary', e.target.value)}
                        />
                        <div>
                          <p className="text-sm font-bold text-gray-800 font-mono">{formData.brand_color_primary}</p>
                          <p className="text-xs text-gray-400">Fondo del nav y degradado del hero</p>
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Color de Botones y Accents</label>
                      <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl">
                        <input
                          type="color"
                          className="w-10 h-10 rounded-lg cursor-pointer border-none bg-transparent"
                          value={formData.brand_color_secondary}
                          onChange={(e) => set('brand_color_secondary', e.target.value)}
                        />
                        <div>
                          <p className="text-sm font-bold text-gray-800 font-mono">{formData.brand_color_secondary}</p>
                          <p className="text-xs text-gray-400">Botones de accion y destacados</p>
                        </div>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Combinaciones rapidas</p>
                      <div className="flex gap-2 flex-wrap">
                        {[
                          { p: '#1e293b', s: '#3b82f6', name: 'Azul Pro' },
                          { p: '#7c3aed', s: '#a855f7', name: 'Morado' },
                          { p: '#b91c1c', s: '#ef4444', name: 'Rojo' },
                          { p: '#065f46', s: '#10b981', name: 'Verde' },
                          { p: '#1e3a5f', s: '#f59e0b', name: 'Dorado' },
                          { p: '#111827', s: '#6366f1', name: 'Dark' },
                        ].map(c => (
                          <button
                            key={c.name}
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, brand_color_primary: c.p, brand_color_secondary: c.s }))}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
                          >
                            <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: c.p }}></span>
                            <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: c.s }}></span>
                            <span className="text-xs text-gray-500">{c.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* TAB: FOOTER */}
                {activeTab === 'footer' && (
                  <div className="space-y-5">
                    <SectionTitle number="5" title="Pie de pagina y Redes" subtitle="Slogan, contacto y redes sociales" />

                    {/* Slogan */}
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Slogan del footer</label>
                      <textarea
                        rows={2}
                        placeholder="Ej: Tu confianza es nuestro motor. Calidad garantizada desde 2010."
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all text-sm resize-none"
                        value={formData.footer_tagline}
                        onChange={(e) => set('footer_tagline', e.target.value)}
                      />
                    </div>

                    {/* WhatsApp */}
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                        <span className="inline-flex items-center gap-1.5">
                          <span className="w-4 h-4 rounded bg-green-500 inline-flex items-center justify-center">
                            <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                          </span>
                          WhatsApp
                        </span>
                      </label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">+57</span>
                        <input
                          type="tel"
                          placeholder="300 000 0000"
                          className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all text-sm"
                          value={formData.whatsapp}
                          onChange={(e) => set('whatsapp', e.target.value)}
                        />
                      </div>
                      <p className="text-xs text-gray-400 mt-1">Boton flotante verde + link en el footer</p>
                    </div>

                    {/* Redes Sociales */}
                    <div>
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Redes Sociales</p>
                      <p className="text-xs text-gray-400 mb-4">Activa las redes que uses y pega el link de tu perfil</p>

                      {/* Instagram */}
                      <div className="mb-3">
                        <div
                          className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 cursor-pointer transition-all ${
                            formData.instagram !== null && formData.instagram !== undefined && formData._igActive
                              ? 'border-pink-400 bg-pink-50'
                              : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                          }`}
                          onClick={() => setFormData(prev => ({ ...prev, _igActive: !prev._igActive, instagram: prev._igActive ? '' : prev.instagram }))}
                        >
                          <div className="w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)' }}>
                            <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                            </svg>
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-bold text-gray-700">Instagram</p>
                            <p className="text-xs text-gray-400">Perfil de tu taller</p>
                          </div>
                          <div className={`w-10 h-5 rounded-full transition-all flex items-center px-0.5 ${formData._igActive ? 'bg-pink-500 justify-end' : 'bg-gray-300 justify-start'}`}>
                            <div className="w-4 h-4 bg-white rounded-full shadow"></div>
                          </div>
                        </div>
                        {formData._igActive && (
                          <input
                            type="url"
                            placeholder="https://instagram.com/mi_taller"
                            className="w-full mt-2 px-4 py-2.5 bg-pink-50 border border-pink-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-400 text-sm transition-all"
                            value={formData.instagram}
                            onChange={(e) => set('instagram', e.target.value)}
                            autoFocus
                          />
                        )}
                      </div>

                      {/* Facebook */}
                      <div className="mb-3">
                        <div
                          className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 cursor-pointer transition-all ${
                            formData._fbActive
                              ? 'border-blue-400 bg-blue-50'
                              : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                          }`}
                          onClick={() => setFormData(prev => ({ ...prev, _fbActive: !prev._fbActive, facebook: prev._fbActive ? '' : prev.facebook }))}
                        >
                          <div className="w-8 h-8 rounded-lg bg-blue-600 flex-shrink-0 flex items-center justify-center">
                            <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
                            </svg>
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-bold text-gray-700">Facebook</p>
                            <p className="text-xs text-gray-400">Pagina de tu taller</p>
                          </div>
                          <div className={`w-10 h-5 rounded-full transition-all flex items-center px-0.5 ${formData._fbActive ? 'bg-blue-500 justify-end' : 'bg-gray-300 justify-start'}`}>
                            <div className="w-4 h-4 bg-white rounded-full shadow"></div>
                          </div>
                        </div>
                        {formData._fbActive && (
                          <input
                            type="url"
                            placeholder="https://facebook.com/mi_taller"
                            className="w-full mt-2 px-4 py-2.5 bg-blue-50 border border-blue-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm transition-all"
                            value={formData.facebook}
                            onChange={(e) => set('facebook', e.target.value)}
                            autoFocus
                          />
                        )}
                      </div>

                      {/* TikTok */}
                      <div className="mb-3">
                        <div
                          className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 cursor-pointer transition-all ${
                            formData._ttActive
                              ? 'border-gray-700 bg-gray-900/5'
                              : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                          }`}
                          onClick={() => setFormData(prev => ({ ...prev, _ttActive: !prev._ttActive, tiktok: prev._ttActive ? '' : prev.tiktok }))}
                        >
                          <div className="w-8 h-8 rounded-lg bg-black flex-shrink-0 flex items-center justify-center">
                            <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V9.05a8.16 8.16 0 0 0 4.77 1.52V7.12a4.85 4.85 0 0 1-1-.43z"/>
                            </svg>
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-bold text-gray-700">TikTok</p>
                            <p className="text-xs text-gray-400">Perfil de tu taller</p>
                          </div>
                          <div className={`w-10 h-5 rounded-full transition-all flex items-center px-0.5 ${formData._ttActive ? 'bg-gray-900 justify-end' : 'bg-gray-300 justify-start'}`}>
                            <div className="w-4 h-4 bg-white rounded-full shadow"></div>
                          </div>
                        </div>
                        {formData._ttActive && (
                          <input
                            type="url"
                            placeholder="https://tiktok.com/@mi_taller"
                            className="w-full mt-2 px-4 py-2.5 bg-gray-50 border border-gray-700 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-700 text-sm transition-all"
                            value={formData.tiktok}
                            onChange={(e) => set('tiktok', e.target.value)}
                            autoFocus
                          />
                        )}
                      </div>
                    </div>

                    {/* Preview mini footer */}
                    {(formData.footer_tagline || formData.whatsapp || formData.instagram || formData.facebook || formData.tiktok) && (
                      <div className="bg-gray-900 rounded-xl p-4">
                        <p className="text-white font-black italic text-sm mb-1">{workshop.name}</p>
                        {formData.footer_tagline && <p className="text-gray-400 text-xs leading-relaxed mb-3">{formData.footer_tagline}</p>}
                        <div className="flex flex-wrap gap-2">
                          {formData.whatsapp && (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 rounded-full text-white text-xs font-bold">
                              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                              {formData.whatsapp}
                            </span>
                          )}
                          {formData.instagram && (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-white text-xs font-bold" style={{ background: 'linear-gradient(135deg, #f09433, #dc2743, #bc1888)' }}>
                              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><circle cx="12" cy="12" r="4"/></svg>
                              Instagram
                            </span>
                          )}
                          {formData.facebook && (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 rounded-full text-white text-xs font-bold">
                              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
                              Facebook
                            </span>
                          )}
                          {formData.tiktok && (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-black rounded-full text-white text-xs font-bold">
                              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V9.05a8.16 8.16 0 0 0 4.77 1.52V7.12a4.85 4.85 0 0 1-1-.43z"/></svg>
                              TikTok
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* URL info */}
            <div className="flex items-center gap-3 px-4 py-3 bg-brand-50 border border-brand-200 rounded-xl">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-brand-500 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
              </svg>
              <div>
                <p className="text-xs text-gray-500">Tu pagina quedara disponible en:</p>
                <p className="text-xs font-black text-brand-600">/taller/{workshop.slug}</p>
              </div>
            </div>
          </div>

          {/* LIVE PREVIEW */}
          <div className="lg:col-span-3 sticky top-24">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Vista Previa en Tiempo Real</p>
              <span className="text-xs bg-green-100 text-green-600 font-semibold px-2.5 py-1 rounded-full">Live</span>
            </div>

            {/* Preview container con altura máxima y scroll */}
            <div className="rounded-2xl border border-gray-200 shadow-xl bg-white max-h-[700px] overflow-y-auto overflow-x-hidden">
              <div className="pointer-events-none transform scale-[0.8] origin-top w-[125%]">
                {/* Combinamos el taller con los datos editados en vivo para la vista previa */}
                <TemplateRenderer workshopData={{ ...workshop, ...formData }} />
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default WorkshopSetup;
